/**
 * Block Types - Discriminated Unions
 * Requirements: 2.1
 */

import type { JSONContent } from '@tiptap/core';

export type BlockType = 'video' | 'basic-content-a' | 'basic-content-b' | 'stylized-text-callout' | 'stylized-numbered-list' | 'stylized-sidebar-callout' | 'comparison-slider' | 'click-expand' | 'workplace-color-spectrum' | 'likert-scale' | 'policy-acknowledgement' | 'electronic-signature' | 'map' | 'categorization' | 'single-choice-answer' | 'freeform-text' | 'lesson-complete' | 'link';

/** Render state of an account-scoped content row. `suppress` hides the block for that account. */
export type RenderState = 'render' | 'suppress';

/**
 * Per-block customization provenance returned by the editor read when a client
 * (account) context is active. `inherited` = no account row (shows base);
 * `override` = account render row (shows account payload); `suppressed` =
 * account suppress row. Absent (undefined) in Standard context.
 */
export type CustomizationState = 'inherited' | 'override' | 'suppressed';

export interface BaseBlock {
  id: string;
  type: BlockType;
  requireInteraction?: boolean;
  interactionMessage?: string;
  successAnimationType?: SuccessAnimationType;
  hideResults?: boolean;
  /**
   * Block-level content lock. A locked block is read-only in the authoring
   * editor for all contexts; only Emtrain (Standard) context may toggle it.
   * Authoring-only semantics — preview/player ignore it. When account-scoped
   * saves land, `locked` mutations must route to the base scope only and must
   * never create account-specific content rows.
   */
  locked?: boolean;
  createdAt: string;
  updatedAt: string;
  /**
   * Client-context customization provenance, populated only by the editor read
   * when an account context is active. Not part of persisted base block content;
   * Standard-context reads leave these undefined.
   */
  customizationState?: CustomizationState;
  renderState?: RenderState;
  /**
   * True when this block's anchor was created for the active account (a
   * client-added block) rather than a base block. Editor-read-only; lets the
   * editor offer delete (own block) vs hide (base block) in client context.
   */
  clientAdded?: boolean;
  /**
   * Learner-facing translation provenance of this block's rendered content for
   * the resolved locale: `'mt'`/`'ht'` for machine/human translation, `null`
   * (or absent) for untranslated source content. Populated by the course
   * resolve/export path only (NA-408, "Show Block IDs") — distinct from the
   * authoring-only `LessonBlockTranslationRecord.translationType` used by the
   * translation-management editor.
   */
  translationType?: 'mt' | 'ht' | null;
}

/**
 * Whether a block is hidden for the active client account — either an explicit
 * account suppression (`customizationState: 'suppressed'`) or a suppressed
 * render state. Single source of truth for the editor and preview reads so the
 * two-marker rule never drifts between views.
 */
export const isBlockSuppressed = (
  block: Pick<BaseBlock, 'customizationState' | 'renderState'>
): boolean => block.customizationState === 'suppressed' || block.renderState === 'suppress';

/**
 * Whether a BASE block has been edited for the active client account — an
 * account `override` row disconnected from the Emtrain standard version.
 * Excludes `clientAdded` blocks: those never had a standard version to
 * disconnect from, even though editing one also stamps `customizationState:
 * 'override'` (the same edit marker `updateBlock` applies to every block
 * touched in client context). Single source of truth for the "Customized"
 * badge/status bar (editor canvas and blocks list) so the two surfaces never
 * drift.
 */
export const isBlockOverridden = (
  block: Pick<BaseBlock, 'customizationState' | 'clientAdded'>
): boolean => block.customizationState === 'override' && !block.clientAdded;

export interface VideoTrack {
  kind: 'captions' | 'subtitles' | 'chapters';
  src: string;
  label: string;
  language: string;
  default?: boolean;
}

/** Canonical h1-h4 title level. Per-block names are aliases kept for API stability. */
export type TitleLevel = 'h1' | 'h2' | 'h3' | 'h4';

export type VideoTitleLevel = TitleLevel;

export interface VideoBlock extends BaseBlock {
  type: 'video';
  url: string;
  posterUrl?: string;
  tracks: VideoTrack[];
  centerstageVideoId?: string;
  title?: string;
  titleLevel?: VideoTitleLevel;
  shortDescription?: string;
  longDescription?: string | JSONContent;
}

export type BasicContentLayout = 'image-top' | 'image-right' | 'image-left' | 'no-image';
export type BasicContentImageSize = 'small' | 'medium' | 'large';
export type BasicContentTitleLevel = TitleLevel;
export type BasicContentBody = string | JSONContent;
export type SuccessAnimationType = 'confetti' | 'pixie-dust' | 'gold-coins';

export interface BasicContentBlockA extends BaseBlock {
  type: 'basic-content-a';
  layout?: BasicContentLayout;
  imageSize?: BasicContentImageSize;
  imageUrl?: string;
  imageAlt: string;
  imageLibraryItemId?: string;
  titleLevel?: BasicContentTitleLevel;
  title: string;
  body: BasicContentBody;
  aspectRatio: string;
}

export interface BasicContentBlockB extends BaseBlock {
  type: 'basic-content-b';
  layout?: BasicContentLayout;
  imageSize?: BasicContentImageSize;
  imageUrl?: string;
  imageAlt: string;
  imageLibraryItemId?: string;
  titleLevel?: BasicContentTitleLevel;
  title: string;
  body: BasicContentBody;
  aspectRatio: string;
}

export type TextCalloutTextSize = 'smaller' | 'standard' | 'larger';

export interface StylizedTextCalloutBlock extends BaseBlock {
  type: 'stylized-text-callout';
  content: JSONContent;
  textSize: TextCalloutTextSize;
}

export interface NumberedListItem {
  id: string;
  content: JSONContent;
}

export type ListStyle = 'numbered' | 'bulleted';
export type StylizedListTitleLevel = TitleLevel;

export interface StylizedNumberedListBlock extends BaseBlock {
  type: 'stylized-numbered-list';
  title?: string;
  titleLevel?: StylizedListTitleLevel;
  items: NumberedListItem[];
  listStyle?: ListStyle;
}

export type SidebarCalloutTitleLevel = StylizedListTitleLevel;

export interface StylizedSidebarCalloutBlock extends BaseBlock {
  type: 'stylized-sidebar-callout';
  title: JSONContent;        // Main content title
  titleLevel?: SidebarCalloutTitleLevel; // Main title level selection (H1/H2/H3)
  body: JSONContent;         // Main content body text
  calloutTitle: JSONContent; // Sidebar callout title (bold-by-default via typed storedMarks)
  calloutBody: JSONContent;  // Sidebar callout body text
}

export type ComparisonSliderContentType = 'text-image' | 'text-only' | 'video';
export type ComparisonSliderImageSize = 'small' | 'large';
export type ComparisonSliderTitleLevel = TitleLevel;

export interface ComparisonSliderOption {
  collapsedLabel?: string;       // Label shown when this option is collapsed (plain text)
  title: JSONContent;            // Option title (bold styling)
  body: JSONContent;             // Option body text (rich text)
  imageUrl?: string;             // Optional image URL
  imageAlt?: string;             // Alt text for image (required if imageUrl provided)
  imageLibraryItemId?: string;    // Authoring library metadata pointer
  videoUrl?: string;             // Optional video URL (video content type)
  videoPosterUrl?: string;       // Video poster/thumbnail URL
  videoPosterImageLibraryItemId?: string;
  videoTitle?: string;           // Video title (plain text)
  videoShortDescription?: string; // Video short description (plain text, max 175 chars)
  centerstageVideoId?: string;   // CenterStage video ID
}

export interface ComparisonSliderBlock extends BaseBlock {
  type: 'comparison-slider';
  contentType: ComparisonSliderContentType; // Controls which fields appear in panels
  imageSize?: ComparisonSliderImageSize;    // Image size preset (small=22%, large=32%)
  titleLevel?: ComparisonSliderTitleLevel;  // Title heading level (H1/H2/H3)
  title: JSONContent;           // Block header
  subtitle: JSONContent;        // Descriptive text below title
  option1: ComparisonSliderOption;
  option2: ComparisonSliderOption;
}

export interface ClickExpandTopic {
  id: string;
  title: string;
  content: JSONContent;
  imageUrl?: string;
  imageAlt?: string;
  imageLibraryItemId?: string;
}

export type ClickExpandType = 'accordion' | 'top-tabs';
export type ClickExpandImageSize = 'large' | 'small';
export type ClickExpandTitleLevel = TitleLevel;

export interface ClickExpandBlock extends BaseBlock {
  type: 'click-expand';
  title: string;
  titleLevel?: ClickExpandTitleLevel;
  description: JSONContent;
  expandType: ClickExpandType;
  imageSize?: ClickExpandImageSize;
  topics: ClickExpandTopic[];
}

export type SpectrumColor = 'green' | 'yellow' | 'orange' | 'red';
export type WorkplaceSpectrumTitleLevel = TitleLevel;

export interface SpectrumColorEntry {
  title: string;
  description: JSONContent;
}

export interface WorkplaceColorSpectrumBlock extends BaseBlock {
  type: 'workplace-color-spectrum';
  /** Question Bank reference; question content resolves server-side at read/export. */
  questionDefinitionId?: number;
  title?: string;
  titleLevel?: WorkplaceSpectrumTitleLevel;
  /**
   * NA-384: true when this block has taken ownership of its title from the
   * Question Bank. Absent means inheriting — but unlike
   * `descriptionOverridden`/`correctAnswerExplanationOverridden`, absence in
   * content predating NA-384 is not left as-is: every pre-existing block was
   * migrated to `titleOverridden: true` at rollout (frozen), because `title`
   * already had a value on every block before this marker existed, and only a
   * question selected after rollout should start out inheriting.
   *
   * Unlike those two fields, an inheriting block's `title` stays persisted in
   * storage (never stripped on save) — resolution overwrites it on every read
   * instead of the server omitting the key, because `title` is a required
   * string on Single Choice and Categorization and cannot be safely omitted.
   */
  titleOverridden?: boolean;
  /**
   * NA-384: the Question Bank's Title for the question this block references,
   * supplied by resolution so the editor can tell whether this block
   * diverges. Read-only and never persisted. Plain-string comparison — title
   * is never rich text, so no JSON canonicalization is needed the way it is
   * for `descriptionBankValue`/`correctAnswerExplanationBankValue`.
   */
  titleBankValue?: string;
  description?: JSONContent;
  /**
   * NA-382: true when this block has taken ownership of its question text
   * (`description`) from the Question Bank. Absent means inheriting, so content
   * predating NA-382 needs no conversion.
   *
   * Ownership is this marker alone — the same three states as NA-313's
   * explanation, blank included. Clearing the field is an override to blank (the
   * learner is shown no question text), not a revert to the bank value; revert is
   * how a block goes back to inheriting.
   *
   * Written together with the text, never on its own. Owned by the
   * default-locale scope, so a translation cannot claim or release ownership —
   * which is why no revert action is offered in a translation view.
   */
  descriptionOverridden?: boolean;
  /**
   * NA-382: the Question Bank's question text for the locale being read,
   * supplied by resolution so the editor can tell whether this block diverges.
   * Read-only and stripped on save — never persist it.
   */
  descriptionBankValue?: JSONContent;
  colors?: Record<SpectrumColor, SpectrumColorEntry>;
  correctAnswer?: SpectrumColor;
  correctAnswerExplanation?: JSONContent;
  /**
   * NA-313: true when this block has taken ownership of
   * `correctAnswerExplanation` from the Question Bank. Absent means inheriting,
   * so content predating NA-313 needs no conversion.
   *
   * Required because the three states — inheriting, overridden with text, and
   * overridden to deliberately blank — cannot be told apart from the text alone.
   * Written together with the text, never on its own. Owned by the
   * default-locale scope (author-api `STRUCTURAL_REFERENCE_FIELDS`), so a
   * translation cannot claim or release ownership of the field.
   */
  correctAnswerExplanationOverridden?: boolean;
  /**
   * NA-313: the Question Bank's explanation for the locale being read, supplied
   * by resolution so the editor can tell whether this block diverges. Read-only
   * and stripped on save — never persist it.
   */
  correctAnswerExplanationBankValue?: JSONContent;
  /**
   * Bank answer-option ids resolved server-side; a label lowercases to its
   * SpectrumColor. Used to attribute learner responses (NA-178).
   */
  options?: { id: string; label: string }[];
}

export type LikertScaleType =
  | 'agreement'
  | 'confidence'
  | 'importance'
  | 'likelihood'
  | 'frequency'
  | 'satisfaction';

export type LikertScaleOptionCount = 5 | 7;
export type LikertScaleTitleLevel = TitleLevel;
export type LikertHealthyResponse = 'none' | 'left' | 'right';

export interface LikertScaleBlock extends BaseBlock {
  type: 'likert-scale';
  /** Question Bank reference; question content resolves server-side at read/export. */
  questionDefinitionId?: number;
  title?: string;
  titleLevel?: LikertScaleTitleLevel;
  /**
   * NA-384: true when this block has taken ownership of its title from the
   * Question Bank. Absent means inheriting — but unlike
   * `descriptionOverridden`/`correctAnswerExplanationOverridden`, absence in
   * content predating NA-384 is not left as-is: every pre-existing block was
   * migrated to `titleOverridden: true` at rollout (frozen), because `title`
   * already had a value on every block before this marker existed, and only a
   * question selected after rollout should start out inheriting.
   *
   * Unlike those two fields, an inheriting block's `title` stays persisted in
   * storage (never stripped on save) — resolution overwrites it on every read
   * instead of the server omitting the key, because `title` is a required
   * string on Single Choice and Categorization and cannot be safely omitted.
   */
  titleOverridden?: boolean;
  /**
   * NA-384: the Question Bank's Title for the question this block references,
   * supplied by resolution so the editor can tell whether this block
   * diverges. Read-only and never persisted. Plain-string comparison — title
   * is never rich text, so no JSON canonicalization is needed the way it is
   * for `descriptionBankValue`/`correctAnswerExplanationBankValue`.
   */
  titleBankValue?: string;
  description?: JSONContent;
  /**
   * NA-382: true when this block has taken ownership of its question text
   * (`description`) from the Question Bank. Absent means inheriting, so content
   * predating NA-382 needs no conversion.
   *
   * Ownership is this marker alone — the same three states as NA-313's
   * explanation, blank included. Clearing the field is an override to blank (the
   * learner is shown no question text), not a revert to the bank value; revert is
   * how a block goes back to inheriting.
   *
   * Written together with the text, never on its own. Owned by the
   * default-locale scope, so a translation cannot claim or release ownership —
   * which is why no revert action is offered in a translation view.
   */
  descriptionOverridden?: boolean;
  /**
   * NA-382: the Question Bank's question text for the locale being read,
   * supplied by resolution so the editor can tell whether this block diverges.
   * Read-only and stripped on save — never persist it.
   */
  descriptionBankValue?: JSONContent;
  scale?: LikertScaleType;
  optionCount?: LikertScaleOptionCount;
  healthyResponse?: LikertHealthyResponse;
  /**
   * Bank answer-option ids resolved server-side, position-ordered to match
   * the labels regenerated from `scale`, so the selection at index n maps to
   * optionIds[n]. Used to attribute learner responses (NA-178).
   */
  optionIds?: string[];
}

export type FreeformTextTitleLevel = TitleLevel;

export interface FreeformTextBlock extends BaseBlock {
  type: 'freeform-text';
  /** Question Bank reference; question content resolves server-side at read/export. */
  questionDefinitionId?: number;
  title?: string;
  titleLevel?: FreeformTextTitleLevel;
  /** NA-382/384-style block-level override of the Title, same contract as LikertScaleBlock's. */
  titleOverridden?: boolean;
  titleBankValue?: string;
  description?: JSONContent;
  /** Same override contract as LikertScaleBlock's `descriptionOverridden`. */
  descriptionOverridden?: boolean;
  descriptionBankValue?: JSONContent;
  /**
   * Freeform Text has no answer options at all - there is nothing analogous
   * to `optionIds`/`options` to resolve, and the 500-character response limit
   * is a fixed constant, never a per-block/per-question setting.
   */
}

export type PolicyType = 'pdf' | 'link';

export type PolicyAcknowledgementTitleLevel = TitleLevel;

export interface PolicyAcknowledgementBlock extends BaseBlock {
  type: 'policy-acknowledgement';
  title: string;
  titleLevel?: PolicyAcknowledgementTitleLevel;
  description: BasicContentBody;
  policyType: PolicyType;
  policyUrl: string;
  // True if the uploaded PDF contains any link annotations.
  // Undefined on legacy blocks — treat as true (open in new window) for safety.
  pdfHasLinks?: boolean;
}

export type LinkTitleLevel = TitleLevel;

// All fields optional (NA-409): blocks auto-save as authored, and a Link
// block is meant to be usable before an author has filled anything in.
export interface LinkBlock extends BaseBlock {
  type: 'link';
  title?: string;
  titleLevel?: LinkTitleLevel;
  description?: BasicContentBody;
  linkText?: string;
  linkUrl?: string;
}

export type ElectronicSignatureTitleLevel = TitleLevel;

export interface ElectronicSignatureBlock extends BaseBlock {
  type: 'electronic-signature';
  title: string;
  titleLevel?: ElectronicSignatureTitleLevel;
  description: BasicContentBody;
  checkboxLabel: string;
}

export type MapTitleLevel = TitleLevel;

export interface MapBlock extends BaseBlock {
  type: 'map';
  mapVersionId: string;
  title: string;
  titleLevel?: MapTitleLevel;
  description: string | JSONContent;
  resolvedMapVersion?: import('./maps').MapVersion;
}

export type CategorizationIconType = 'emoji' | 'image';

export interface CategorizationIcon {
  type: CategorizationIconType;
  value: string; // emoji character or image URL
  imageLibraryItemId?: string;
}

export interface CategorizationItem {
  id: string;
  label: string;
  icon?: CategorizationIcon;
  correctCategoryId: string;
}

export interface CategorizationCategory {
  id: string;
  label: string;
  icon?: CategorizationIcon;
}

export type CategorizationTitleLevel = TitleLevel;

export interface CategorizationBlock extends BaseBlock {
  type: 'categorization';
  /** Question Bank reference; question content resolves server-side at read/export. */
  questionDefinitionId?: number;
  title: string;
  titleLevel?: CategorizationTitleLevel;
  /**
   * NA-384: true when this block has taken ownership of its title from the
   * Question Bank. Absent means inheriting — but unlike
   * `descriptionOverridden`, absence in content predating NA-384 is not left
   * as-is: every pre-existing block was migrated to `titleOverridden: true`
   * at rollout (frozen), because `title` already had a value on every block
   * before this marker existed, and only a question selected after rollout
   * should start out inheriting.
   *
   * Unlike `description`, an inheriting block's `title` stays persisted in
   * storage (never stripped on save) — resolution overwrites it on every read
   * instead of the server omitting the key, because `title` is a required
   * string on this type (and Single Choice) and cannot be safely omitted.
   */
  titleOverridden?: boolean;
  /**
   * NA-384: the Question Bank's Title for the question this block references,
   * supplied by resolution so the editor can tell whether this block
   * diverges. Read-only and never persisted. Plain-string comparison — title
   * is never rich text, so no JSON canonicalization is needed the way it is
   * for `descriptionBankValue`.
   */
  titleBankValue?: string;
  description?: JSONContent;
  /**
   * NA-382: true when this block has taken ownership of its question text
   * (`description`) from the Question Bank. Absent means inheriting, so content
   * predating NA-382 needs no conversion.
   *
   * Ownership is this marker alone — the same three states as NA-313's
   * explanation, blank included. Clearing the field is an override to blank (the
   * learner is shown no question text), not a revert to the bank value; revert is
   * how a block goes back to inheriting.
   *
   * Written together with the text, never on its own. Owned by the
   * default-locale scope, so a translation cannot claim or release ownership —
   * which is why no revert action is offered in a translation view.
   */
  descriptionOverridden?: boolean;
  /**
   * NA-382: the Question Bank's question text for the locale being read,
   * supplied by resolution so the editor can tell whether this block diverges.
   * Read-only and stripped on save — never persist it.
   */
  descriptionBankValue?: JSONContent;
  instructionText?: string;
  categories: CategorizationCategory[];
  items: CategorizationItem[];
  correctFeedbackHeading?: string;
  correctFeedbackBody?: JSONContent;
  incorrectFeedbackHeading?: string;
  incorrectFeedbackBody?: JSONContent;
  submitButtonLabel?: string;
}

export type SingleChoiceAnswerTitleLevel = TitleLevel;

export interface SingleChoiceOption {
  id: string;
  label: string;
}

export interface SingleChoiceAnswerBlock extends BaseBlock {
  type: 'single-choice-answer';
  /** Question Bank reference; question content resolves server-side at read/export. */
  questionDefinitionId?: number;
  title: string;
  titleLevel?: SingleChoiceAnswerTitleLevel;
  /**
   * NA-384: true when this block has taken ownership of its title from the
   * Question Bank. Absent means inheriting — but unlike
   * `descriptionOverridden`/`correctExplanationOverridden`, absence in
   * content predating NA-384 is not left as-is: every pre-existing block was
   * migrated to `titleOverridden: true` at rollout (frozen), because `title`
   * already had a value on every block before this marker existed, and only a
   * question selected after rollout should start out inheriting.
   *
   * Unlike those two fields, an inheriting block's `title` stays persisted in
   * storage (never stripped on save) — resolution overwrites it on every read
   * instead of the server omitting the key, because `title` is a required
   * string on this type (and Categorization) and cannot be safely omitted.
   */
  titleOverridden?: boolean;
  /**
   * NA-384: the Question Bank's Title for the question this block references,
   * supplied by resolution so the editor can tell whether this block
   * diverges. Read-only and never persisted. Plain-string comparison — title
   * is never rich text, so no JSON canonicalization is needed the way it is
   * for `descriptionBankValue`/`correctExplanationBankValue`.
   */
  titleBankValue?: string;
  description?: JSONContent;
  /**
   * NA-382: true when this block has taken ownership of its question text
   * (`description`) from the Question Bank. Absent means inheriting, so content
   * predating NA-382 needs no conversion.
   *
   * Ownership is this marker alone — the same three states as NA-313's
   * explanation, blank included. Clearing the field is an override to blank (the
   * learner is shown no question text), not a revert to the bank value; revert is
   * how a block goes back to inheriting.
   *
   * Written together with the text, never on its own. Owned by the
   * default-locale scope, so a translation cannot claim or release ownership —
   * which is why no revert action is offered in a translation view.
   */
  descriptionOverridden?: boolean;
  /**
   * NA-382: the Question Bank's question text for the locale being read,
   * supplied by resolution so the editor can tell whether this block diverges.
   * Read-only and stripped on save — never persist it.
   */
  descriptionBankValue?: JSONContent;
  options: SingleChoiceOption[];
  gated: boolean;
  correctOptionId?: string;
  correctExplanation?: JSONContent;
  /**
   * NA-313: true when this block has taken ownership of `correctExplanation`
   * from the Question Bank. Absent means inheriting, so content predating
   * NA-313 needs no conversion.
   *
   * Required because the three states — inheriting, overridden with text, and
   * overridden to deliberately blank — cannot be told apart from the text alone.
   * Written together with the text, never on its own. Owned by the
   * default-locale scope (author-api `STRUCTURAL_REFERENCE_FIELDS`), so a
   * translation cannot claim or release ownership of the field. Cleared
   * alongside the text when the block is ungated, since an ungated block has no
   * such field.
   */
  correctExplanationOverridden?: boolean;
  /**
   * NA-313: the Question Bank's explanation for the locale being read, supplied
   * by resolution so the editor can tell whether this block diverges. Read-only
   * and stripped on save — never persist it.
   */
  correctExplanationBankValue?: JSONContent;
  healthyOptionId?: string;
}

export type LessonCompleteTitleLevel = TitleLevel;

export interface LessonCompleteBlock extends BaseBlock {
  type: 'lesson-complete';
  imageUrl?: string;
  imageAlt?: string;
  imageLibraryItemId?: string;
  title: string;
  titleLevel?: LessonCompleteTitleLevel;
  description: JSONContent;
  successAnimationType?: SuccessAnimationType;
}

export type CourseBlock = VideoBlock | BasicContentBlockA | BasicContentBlockB | StylizedTextCalloutBlock | StylizedNumberedListBlock | StylizedSidebarCalloutBlock | ComparisonSliderBlock | ClickExpandBlock | WorkplaceColorSpectrumBlock | LikertScaleBlock | PolicyAcknowledgementBlock | ElectronicSignatureBlock | MapBlock | CategorizationBlock | SingleChoiceAnswerBlock | FreeformTextBlock | LessonCompleteBlock | LinkBlock;
